import { createAction } from '@reduxjs/toolkit';
import { IErrorType } from '../../constants/types';
import type {
    IBuzzbridgeLoyaltyDashboard,
    IBuzzbridgeMemberDetail,
    IBuzzbridgeNetworkResponse,
    IBuzzbridgeNetworkInvite,
    IBuzzbridgeRedemptionProgram,
    IBuzzbridgeRedemptionResult,
    IBuzzbridgeLoyaltyWalletResponse,
    IBuzzbridgeLoyaltyWithdrawPayload,
    IBuzzbridgeLoyaltyWithdrawResult,
    IAdminLoyaltyWithdrawalsResponse,
    IAdminPatchLoyaltyWithdrawalPayload,
    IBuzzbridgeLoyaltyWithdrawal,
    IAdminLoyaltyGrantRequest,
    IAdminLoyaltyGrantResult,
    IAdminLoyaltyOverview,
    IAdminLoyaltyLeaderboardResponse,
    IAdminLoyaltyUserDetail,
    AdminLoyaltyLeaderboardSortBy,
} from '../../types/global/buzzbridgeLoyalty.types';

export const types = {
    GET_LOYALTY_DASHBOARD: 'GET_LOYALTY_DASHBOARD',
    GET_LOYALTY_DASHBOARD_SUCCESS: 'GET_LOYALTY_DASHBOARD_SUCCESS',
    GET_LOYALTY_DASHBOARD_FAIL: 'GET_LOYALTY_DASHBOARD_FAIL',

    GET_LOYALTY_NETWORK: 'GET_LOYALTY_NETWORK',
    GET_LOYALTY_NETWORK_SUCCESS: 'GET_LOYALTY_NETWORK_SUCCESS',
    GET_LOYALTY_NETWORK_FAIL: 'GET_LOYALTY_NETWORK_FAIL',

    GET_LOYALTY_MEMBER: 'GET_LOYALTY_MEMBER',
    GET_LOYALTY_MEMBER_SUCCESS: 'GET_LOYALTY_MEMBER_SUCCESS',
    GET_LOYALTY_MEMBER_FAIL: 'GET_LOYALTY_MEMBER_FAIL',

    GET_LOYALTY_INVITE: 'GET_LOYALTY_INVITE',
    GET_LOYALTY_INVITE_SUCCESS: 'GET_LOYALTY_INVITE_SUCCESS',
    GET_LOYALTY_INVITE_FAIL: 'GET_LOYALTY_INVITE_FAIL',

    GET_REDEMPTION_CATALOG: 'GET_REDEMPTION_CATALOG',
    GET_REDEMPTION_CATALOG_SUCCESS: 'GET_REDEMPTION_CATALOG_SUCCESS',
    GET_REDEMPTION_CATALOG_FAIL: 'GET_REDEMPTION_CATALOG_FAIL',

    REDEEM_POINTS: 'REDEEM_POINTS',
    REDEEM_POINTS_SUCCESS: 'REDEEM_POINTS_SUCCESS',
    REDEEM_POINTS_FAIL: 'REDEEM_POINTS_FAIL',

    GET_LOYALTY_WALLET: 'GET_LOYALTY_WALLET',
    GET_LOYALTY_WALLET_SUCCESS: 'GET_LOYALTY_WALLET_SUCCESS',
    GET_LOYALTY_WALLET_FAIL: 'GET_LOYALTY_WALLET_FAIL',

    REQUEST_LOYALTY_WITHDRAWAL: 'REQUEST_LOYALTY_WITHDRAWAL',
    REQUEST_LOYALTY_WITHDRAWAL_SUCCESS: 'REQUEST_LOYALTY_WITHDRAWAL_SUCCESS',
    REQUEST_LOYALTY_WITHDRAWAL_FAIL: 'REQUEST_LOYALTY_WITHDRAWAL_FAIL',

    GET_ADMIN_LOYALTY_WITHDRAWALS: 'GET_ADMIN_LOYALTY_WITHDRAWALS',
    GET_ADMIN_LOYALTY_WITHDRAWALS_SUCCESS: 'GET_ADMIN_LOYALTY_WITHDRAWALS_SUCCESS',
    GET_ADMIN_LOYALTY_WITHDRAWALS_FAIL: 'GET_ADMIN_LOYALTY_WITHDRAWALS_FAIL',

    PATCH_ADMIN_LOYALTY_WITHDRAWAL: 'PATCH_ADMIN_LOYALTY_WITHDRAWAL',
    PATCH_ADMIN_LOYALTY_WITHDRAWAL_SUCCESS: 'PATCH_ADMIN_LOYALTY_WITHDRAWAL_SUCCESS',
    PATCH_ADMIN_LOYALTY_WITHDRAWAL_FAIL: 'PATCH_ADMIN_LOYALTY_WITHDRAWAL_FAIL',

    ADMIN_GRANT_LOYALTY: 'ADMIN_GRANT_LOYALTY',
    ADMIN_GRANT_LOYALTY_SUCCESS: 'ADMIN_GRANT_LOYALTY_SUCCESS',
    ADMIN_GRANT_LOYALTY_FAIL: 'ADMIN_GRANT_LOYALTY_FAIL',
    CLEAR_ADMIN_GRANT_LOYALTY: 'CLEAR_ADMIN_GRANT_LOYALTY',

    GET_ADMIN_LOYALTY_OVERVIEW: 'GET_ADMIN_LOYALTY_OVERVIEW',
    GET_ADMIN_LOYALTY_OVERVIEW_SUCCESS: 'GET_ADMIN_LOYALTY_OVERVIEW_SUCCESS',
    GET_ADMIN_LOYALTY_OVERVIEW_FAIL: 'GET_ADMIN_LOYALTY_OVERVIEW_FAIL',

    GET_ADMIN_LOYALTY_LEADERBOARD: 'GET_ADMIN_LOYALTY_LEADERBOARD',
    GET_ADMIN_LOYALTY_LEADERBOARD_SUCCESS: 'GET_ADMIN_LOYALTY_LEADERBOARD_SUCCESS',
    GET_ADMIN_LOYALTY_LEADERBOARD_FAIL: 'GET_ADMIN_LOYALTY_LEADERBOARD_FAIL',

    GET_ADMIN_LOYALTY_USER: 'GET_ADMIN_LOYALTY_USER',
    GET_ADMIN_LOYALTY_USER_SUCCESS: 'GET_ADMIN_LOYALTY_USER_SUCCESS',
    GET_ADMIN_LOYALTY_USER_FAIL: 'GET_ADMIN_LOYALTY_USER_FAIL',
    CLEAR_ADMIN_LOYALTY_USER: 'CLEAR_ADMIN_LOYALTY_USER',
} as const;

export interface IRedeemPointsPayload {
    program_id: string;
}

export interface IGetAdminLoyaltyWithdrawalsParams {
    status?: string;
    page?: number;
    limit?: number;
}

export interface IPatchAdminLoyaltyWithdrawalArgs {
    withdrawalId: string;
    payload: IAdminPatchLoyaltyWithdrawalPayload;
}

export interface IGetLoyaltyNetworkParams {
    search?: string;
    page?: number;
    limit?: number;
    days?: 30 | 90;
}

export interface IGetAdminLoyaltyLeaderboardParams {
    sort_by?: AdminLoyaltyLeaderboardSortBy;
    search?: string;
    page?: number;
    limit?: number;
}

export const getLoyaltyDashboard = createAction(types.GET_LOYALTY_DASHBOARD);
export const getLoyaltyDashboardSuccess = createAction<IBuzzbridgeLoyaltyDashboard>(
    types.GET_LOYALTY_DASHBOARD_SUCCESS,
);
export const getLoyaltyDashboardFail = createAction<IErrorType>(types.GET_LOYALTY_DASHBOARD_FAIL);

export const getLoyaltyNetwork = createAction<IGetLoyaltyNetworkParams | undefined>(types.GET_LOYALTY_NETWORK);
export const getLoyaltyNetworkSuccess = createAction<IBuzzbridgeNetworkResponse>(types.GET_LOYALTY_NETWORK_SUCCESS);
export const getLoyaltyNetworkFail = createAction<IErrorType>(types.GET_LOYALTY_NETWORK_FAIL);

export const getLoyaltyMember = createAction<string>(types.GET_LOYALTY_MEMBER);
export const getLoyaltyMemberSuccess = createAction<IBuzzbridgeMemberDetail>(types.GET_LOYALTY_MEMBER_SUCCESS);
export const getLoyaltyMemberFail = createAction<IErrorType>(types.GET_LOYALTY_MEMBER_FAIL);

export const getLoyaltyInvite = createAction(types.GET_LOYALTY_INVITE);
export const getLoyaltyInviteSuccess = createAction<IBuzzbridgeNetworkInvite>(types.GET_LOYALTY_INVITE_SUCCESS);
export const getLoyaltyInviteFail = createAction<IErrorType>(types.GET_LOYALTY_INVITE_FAIL);

export const getRedemptionCatalog = createAction(types.GET_REDEMPTION_CATALOG);
export const getRedemptionCatalogSuccess = createAction<IBuzzbridgeRedemptionProgram[]>(
    types.GET_REDEMPTION_CATALOG_SUCCESS,
);
export const getRedemptionCatalogFail = createAction<IErrorType>(types.GET_REDEMPTION_CATALOG_FAIL);

export const redeemPoints = createAction<IRedeemPointsPayload>(types.REDEEM_POINTS);
export const redeemPointsSuccess = createAction<IBuzzbridgeRedemptionResult>(types.REDEEM_POINTS_SUCCESS);
export const redeemPointsFail = createAction<IErrorType>(types.REDEEM_POINTS_FAIL);

export const getLoyaltyWallet = createAction(types.GET_LOYALTY_WALLET);
export const getLoyaltyWalletSuccess = createAction<IBuzzbridgeLoyaltyWalletResponse>(types.GET_LOYALTY_WALLET_SUCCESS);
export const getLoyaltyWalletFail = createAction<IErrorType>(types.GET_LOYALTY_WALLET_FAIL);

export const requestLoyaltyWithdrawal = createAction<IBuzzbridgeLoyaltyWithdrawPayload>(
    types.REQUEST_LOYALTY_WITHDRAWAL,
);
export const requestLoyaltyWithdrawalSuccess = createAction<IBuzzbridgeLoyaltyWithdrawResult>(
    types.REQUEST_LOYALTY_WITHDRAWAL_SUCCESS,
);
export const requestLoyaltyWithdrawalFail = createAction<IErrorType>(types.REQUEST_LOYALTY_WITHDRAWAL_FAIL);

export const getAdminLoyaltyWithdrawals = createAction<IGetAdminLoyaltyWithdrawalsParams | undefined>(
    types.GET_ADMIN_LOYALTY_WITHDRAWALS,
);
export const getAdminLoyaltyWithdrawalsSuccess = createAction<IAdminLoyaltyWithdrawalsResponse>(
    types.GET_ADMIN_LOYALTY_WITHDRAWALS_SUCCESS,
);
export const getAdminLoyaltyWithdrawalsFail = createAction<IErrorType>(types.GET_ADMIN_LOYALTY_WITHDRAWALS_FAIL);

export const patchAdminLoyaltyWithdrawal = createAction<IPatchAdminLoyaltyWithdrawalArgs>(
    types.PATCH_ADMIN_LOYALTY_WITHDRAWAL,
);
export const patchAdminLoyaltyWithdrawalSuccess = createAction<IBuzzbridgeLoyaltyWithdrawal>(
    types.PATCH_ADMIN_LOYALTY_WITHDRAWAL_SUCCESS,
);
export const patchAdminLoyaltyWithdrawalFail = createAction<IErrorType>(types.PATCH_ADMIN_LOYALTY_WITHDRAWAL_FAIL);

export const adminGrantLoyalty = createAction<IAdminLoyaltyGrantRequest>(types.ADMIN_GRANT_LOYALTY);
export const adminGrantLoyaltySuccess = createAction<IAdminLoyaltyGrantResult>(types.ADMIN_GRANT_LOYALTY_SUCCESS);
export const adminGrantLoyaltyFail = createAction<IErrorType>(types.ADMIN_GRANT_LOYALTY_FAIL);
export const clearAdminGrantLoyalty = createAction(types.CLEAR_ADMIN_GRANT_LOYALTY);

export const getAdminLoyaltyOverview = createAction(types.GET_ADMIN_LOYALTY_OVERVIEW);
export const getAdminLoyaltyOverviewSuccess = createAction<IAdminLoyaltyOverview>(
    types.GET_ADMIN_LOYALTY_OVERVIEW_SUCCESS,
);
export const getAdminLoyaltyOverviewFail = createAction<IErrorType>(types.GET_ADMIN_LOYALTY_OVERVIEW_FAIL);

export const getAdminLoyaltyLeaderboard = createAction<IGetAdminLoyaltyLeaderboardParams | undefined>(
    types.GET_ADMIN_LOYALTY_LEADERBOARD,
);
export const getAdminLoyaltyLeaderboardSuccess = createAction<IAdminLoyaltyLeaderboardResponse>(
    types.GET_ADMIN_LOYALTY_LEADERBOARD_SUCCESS,
);
export const getAdminLoyaltyLeaderboardFail = createAction<IErrorType>(types.GET_ADMIN_LOYALTY_LEADERBOARD_FAIL);

export const getAdminLoyaltyUser = createAction<string>(types.GET_ADMIN_LOYALTY_USER);
export const getAdminLoyaltyUserSuccess = createAction<IAdminLoyaltyUserDetail>(types.GET_ADMIN_LOYALTY_USER_SUCCESS);
export const getAdminLoyaltyUserFail = createAction<IErrorType>(types.GET_ADMIN_LOYALTY_USER_FAIL);
export const clearAdminLoyaltyUser = createAction(types.CLEAR_ADMIN_LOYALTY_USER);
